import React, { useEffect } from 'react';
import { ImageUploadWrapper } from './ImageUpload.styled';
import { FileDropzone, Typography, useTheme } from '@sky-distribution/react-commons-ui';
import { getCoordinates } from './ImageUpload.util';
import { useTypedTranslate } from '../../translations/useTypedTranslate';
import { useConfigValuesContext } from '../../common/contexts/ConfigValues/useConfigValuesContext';
import * as Sentry from '@sentry/react';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '../../common/constants/defaultStaticUrls';
import { icons } from '../../icons';
import { TENANT_NAME, Tenant } from '../../config/tenants';

type ImageUploadProps = {
  setFieldValue: (field: string, value: File | string | boolean | undefined) => void;
};

const MAX_UPLOAD_IMAGE_SIZE_BYTES = 4 * 1024 * 1024;

const iconsByTenant = {
  [Tenant.INTER_RISK]: {
    UploadIcon: icons.interrisk.UploadFile,
    FileIcon: icons.interrisk.FileType,
  },
  [Tenant.TUW]: {
    UploadIcon: icons.tuw.UploadFile,
    FileIcon: icons.tuw.FileType,
  },
  [Tenant.COMPENSA]: undefined,
} as const;

export const ImageUpload = ({ setFieldValue }: ImageUploadProps): JSX.Element => {
  const theme = useTheme();
  const { t } = useTypedTranslate();
  const navigate = useNavigate();
  const [files, setFiles] = React.useState<File[]>([]);
  const { configValues } = useConfigValuesContext();

  const maxImageSizeBytes = Math.min(configValues.maxImageSizeBytes, MAX_UPLOAD_IMAGE_SIZE_BYTES);

  const handleFileLimitError = (candidateFiles?: FileList | null): void => {
    if (!candidateFiles || candidateFiles.length === 0) {
      return;
    }

    const hasOversizedFile = Array.from(candidateFiles).some((file) => file.size > maxImageSizeBytes);
    setFieldValue('fileSizeExceeded', hasOversizedFile);
  };

  useEffect(() => {
    const selectedFile = files[0];

    setFieldValue('file', selectedFile);
    if (!selectedFile) {
      return;
    }

    setFieldValue('fileSizeExceeded', false);

    getCoordinates(selectedFile)
      .then((res) => {
        setFieldValue('latitude', res.latitude);
        setFieldValue('longitude', res.longitude);
      })
      .catch((err: unknown) => {
        Sentry.captureException(err);
        setFiles([]);
        navigate(ROUTES.LOCALIZATION_ERROR);
      });
  }, [files, maxImageSizeBytes, navigate, setFieldValue]);

  const tenantIcons = iconsByTenant[TENANT_NAME];
  const isTuw = TENANT_NAME === Tenant.TUW;

  return (
    <ImageUploadWrapper
      data-testid="image-upload"
      onDropCapture={(event) => handleFileLimitError(event.dataTransfer?.files)}
      onChangeCapture={(event) => handleFileLimitError((event.target as HTMLInputElement).files)}
    >
      <Typography variant="h4" color={theme.palette.text.header}>
        {t('UPLOAD_IMAGE')}
      </Typography>
      <FileDropzone
        setFiles={setFiles}
        maxFiles={1}
        desktopText={t('DESKTOP_UPLOAD_IMAGE_TITLE')}
        additionalText={t('DESKTOP_UPLOAD_IMAGE_ADDITIONAL_TEXT')}
        buttonText={isTuw ? t('SELECT_FILE_FROM_DISK') : undefined}
        mobileText={t('MOBILE_UPLOAD_IMAGE_TITLE')}
        files={files}
        maxSize={maxImageSizeBytes}
        UploadIcon={tenantIcons?.UploadIcon}
        FileIcon={tenantIcons?.FileIcon}
      />
    </ImageUploadWrapper>
  );
};
